export interface HostnameParts {
  tld: string;
  domain: string;
  subdomain: string;
}

/**
 * Parses a hostname into its individual components.
 * @param hostname - A hostname consisting of one or more domain parts (e.g., 'nike.employee.tuition.io')
 * @returns an object containing { subdomain, domain, tld }
 * @throws Error if hostname is invalid
 *
 * @example
 * parseHostname('nike.employee.tuition.io')
 * // => { subdomain: 'nike.employee', domain: 'tuition', tld: 'io' }
 *
 * parseHostname('localhost')
 * // => { subdomain: '', domain: 'localhost', tld: '' }
 */
export function parseHostname(hostname: string): HostnameParts {
  // Basic validation of the hostname
  if (
    !hostname ||
    typeof hostname !== 'string' ||
    hostname.includes('/') ||
    hostname.includes(':')
  ) {
    throw new Error(`'${hostname}' is not a valid hostname`);
  }

  if (hostname === 'localhost') {
    return { domain: 'localhost', tld: '', subdomain: '' };
  }

  const parts = hostname.split('.').filter(Boolean);

  // Ensure there are enough parts for a valid domain
  if (parts.length < 2) {
    throw new Error(`'${hostname}' is not a valid domain`);
  }

  const tld = parts.pop() || '';
  const domain = parts.pop() || '';
  const subdomain = parts.join('.');

  return { tld, domain, subdomain };
}

/**
 * Returns only the first subdomain segment, or an empty string without a subdomain. Defaults to
 * window.location.hostname; nike.shell.tio-stg.io yields nike.
 */
export function getSubdomain(hostname: string = window.location.hostname): string {
  try {
    const { subdomain } = parseHostname(hostname);
    return subdomain?.split('.')[0] || '';
  } catch {
    return '';
  }
}

/**
 * Checks if the current hostname is localhost.
 * @param hostname - The hostname to check (defaults to window.location.hostname)
 */
export function isLocalhost(hostname: string = window.location.hostname): boolean {
  return hostname === 'localhost';
}

/**
 * Constructs a partner logo URL from a subdomain.
 * Falls back to 'tuition' if no subdomain is provided.
 *
 * @param subdomain - The partner subdomain (e.g., 'nike')
 * @param environment - The TIO environment (e.g., 'prd', 'stg', 'vdk')
 * @returns The full S3 URL to the partner logo
 *
 * @example
 * getPartnerLogoUrl('nike', 'prd')
 * // => 'https://tuitionio-prd-images.s3.amazonaws.com/partners/nike.png'
 */
export function getPartnerLogoUrl(subdomain: string | undefined, environment: string): string {
  const partnerSlug = subdomain || 'tuition';
  return `https://tuitionio-${environment}-images.s3.amazonaws.com/partners/${partnerSlug}.png`;
}

/**
 * The origin a partner is served from, derived by swapping the first subdomain
 * segment of the current host.
 *
 * A company's branding and the `x-auth-key` the API scopes requests by are both
 * resolved from the subdomain, so acting as another company means being served
 * from its own origin rather than swapping context in place.
 *
 * @param subdomain the target company's subdomain
 * @param location overrides for testing; defaults to the live location
 * @returns the absolute origin, or undefined when there is nothing to swap
 *   (no subdomain to replace, e.g. localhost, or already there)
 *
 * @example
 * partnerOrigin('globex', { hostname: 'nike.employee.tuition.io', protocol: 'https:' })
 * // => 'https://globex.employee.tuition.io'
 */
export function partnerOrigin(
  subdomain: string | undefined,
  {
    hostname = window.location.hostname,
    protocol = window.location.protocol,
    port = window.location.port,
  }: { hostname?: string; protocol?: string; port?: string } = {}
): string | undefined {
  if (!subdomain) return undefined;

  const current = getSubdomain(hostname);
  if (!current || current === subdomain) return undefined;

  const parts = hostname.split('.');
  parts[0] = subdomain;
  return `${protocol}//${parts.join('.')}${port ? `:${port}` : ''}`;
}
